/**
 * inboxDetailActions — what an opened update can DO, derived from its data.
 *
 * The reading pane ends in one footer with a single primary action. Which
 * destination that is follows from the notification's typed `actions` (the
 * closed union in `@amoreg/contracts`), never from agent-supplied text: the
 * app owns every label, route and variant.
 *
 * Kept separate from the renderers so the choice is a pure function with its
 * own tests, and so the body and the footer cannot disagree about it — the
 * body drops a per-row open control exactly when the footer already carries
 * that record's destination.
 *
 * @module components/inbox/inboxDetailActions
 */
import type { InboxAgentNotification } from "@amoreg/contracts";

/**
 * Which linked records the app could actually resolve in the acting
 * organization. `null` for a kind means "not known" — still loading, or the
 * lookup itself is unavailable — and is treated optimistically, because a
 * spinner must not demote the footer's action and then swap it underneath a
 * reader's cursor.
 */
export interface LinkedRecordAvailability {
  readonly findings: ReadonlySet<string> | null;
  readonly suggestions: ReadonlySet<string> | null;
}

/** A closed set of destinations; the renderer maps each to a typed route. */
export type NotificationDestination =
  | { readonly kind: "finding"; readonly findingId: string }
  | { readonly kind: "findings" }
  | { readonly kind: "suggestion"; readonly recommendationId: string }
  | { readonly kind: "suggestions" }
  | { readonly kind: "monitoring" }
  | { readonly kind: "workflow-run"; readonly workflowId: string; readonly runId: string };

/** Linked record ids of one kind, de-duplicated, in the order the agent sent them. */
export function notificationLinkedIds(
  item: InboxAgentNotification,
  kind: "finding" | "suggestion",
): readonly string[] {
  return [
    ...new Set(
      item.actions?.flatMap((action) =>
        action.kind === "finding" && kind === "finding"
          ? [action.findingId]
          : action.kind === "suggestion" && kind === "suggestion"
            ? [action.recommendationId]
            : [],
      ) ?? [],
    ),
  ];
}

/**
 * The one action the footer leads with. Findings outrank suggestions, which
 * outrank monitoring, matching the order the body lays the sections out. A
 * single linked record opens that record; several open their list, because a
 * footer with four identical "Open finding" buttons names nothing.
 *
 * A single record the organization can no longer resolve — deleted, or never
 * visible to this reader — falls back to the list as well: the body already
 * says it is gone, and the footer must not offer to open it.
 */
export function notificationPrimaryDestination(
  item: InboxAgentNotification,
  available?: LinkedRecordAvailability | null,
): NotificationDestination | null {
  const findings = notificationLinkedIds(item, "finding");
  if (findings.length === 1 && findings[0])
    return available?.findings && !available.findings.has(findings[0])
      ? { kind: "findings" }
      : { kind: "finding", findingId: findings[0] };
  if (findings.length > 1) return { kind: "findings" };
  const suggestions = notificationLinkedIds(item, "suggestion");
  if (suggestions.length === 1 && suggestions[0])
    return available?.suggestions && !available.suggestions.has(suggestions[0])
      ? { kind: "suggestions" }
      : { kind: "suggestion", recommendationId: suggestions[0] };
  if (suggestions.length > 1) return { kind: "suggestions" };
  if (item.actions?.some((action) => action.kind === "monitoring")) return { kind: "monitoring" };
  // A workflow's Notify step: the run it is about.
  const run = item.actions?.find((action) => action.kind === "workflow-run");
  if (run?.kind === "workflow-run") return { kind: "workflow-run", workflowId: run.workflowId, runId: run.runId };
  return null;
}

/**
 * The destinations the footer carries beside its primary. Monitoring names no
 * record, so it never wins the primary slot from a finding or suggestion — but
 * an update that links both must still be able to open the Monitor page.
 */
export function notificationSecondaryDestinations(
  item: InboxAgentNotification,
  available?: LinkedRecordAvailability | null,
): ReadonlyArray<NotificationDestination> {
  const primary = notificationPrimaryDestination(item, available);
  if (!primary || primary.kind === "monitoring") return [];
  return item.actions?.some((action) => action.kind === "monitoring")
    ? [{ kind: "monitoring" }]
    : [];
}

/** Sentence case, app-owned. */
export function notificationDestinationLabel(destination: NotificationDestination): string {
  switch (destination.kind) {
    case "finding":
      return "Open finding";
    case "findings":
      return "Open findings";
    case "suggestion":
      return "Open suggestion";
    case "suggestions":
      return "Open suggestions";
    case "monitoring":
      return "Open monitoring";
    case "workflow-run":
      return "Open run";
  }
}

/**
 * Whether a linked-record row still needs its own open control: only when the
 * footer's single primary cannot stand for it.
 */
export function showsRowDestination(
  item: InboxAgentNotification,
  kind: "finding" | "suggestion",
  available?: LinkedRecordAvailability | null,
): boolean {
  const primary = notificationPrimaryDestination(item, available);
  if (!primary) return true;
  if (kind === "finding") return primary.kind !== "finding";
  return primary.kind !== "suggestion";
}
